import './StatusChip.css';
import { t } from '../copy/strings';
import type { Gender } from '../format/agree';
import { fmtSections } from '../format/numbers';
import { Chip, type ChipTone } from './Chip';

/**
 * Tipos próprios das props (FE §4.2: `components/` não importa `contracts/`, salvo os tipos estáticos). `CountStatus` é
 * igual ao de `contracts/tally` e `Decision` é o subconjunto que o chip lê (`kind`): o dono passa os do `PlaceView` sem
 * conversão.
 */
type CountStatus = 'no_data' | 'counting' | 'complete';
interface Decision {
  kind: 'math' | 'tse';
}

/** Situação exibida: a de `status`, salvo quando há decisão (a do TSE prevalece sobre a matemática). */
export type StatusSituation = CountStatus | 'math_decision' | 'tse_result';

export interface StatusChipProps {
  status: CountStatus;
  /** Fração de seções totalizadas (0–1); vira `{S}` de "Apurando {S}" por `fmtSections` (truncado, §13.4.2). */
  sectionsFraction: number;
  /** `PlaceView.tseResult ?? PlaceView.mathDecision`; o `kind` decide entre "Eleito/Eleita" e "Definido". */
  decision: Decision | null;
  /** Gênero do vencedor, para "Eleito"/"Eleita" (§13.4.5). */
  gender: Gender | null;
  /** Sobrescreve o texto só nas situações definidas (usado pelo `HeroKicker`, §7.3). */
  label?: string;
  /** `true` quando a linha inteira já tem `aria-label` próprio (ex.: `RaceRow`), para não duplicar a leitura. */
  ariaHidden?: boolean;
}

function situationOf(status: CountStatus, decision: Decision | null): StatusSituation {
  if (decision?.kind === 'tse') return 'tse_result';
  if (decision?.kind === 'math') return 'math_decision';
  return status;
}

function view(situation: StatusSituation, p: StatusChipProps): { text: string; tone: ChipTone } {
  switch (situation) {
    case 'no_data':
      return { text: t('chip.status.waiting'), tone: 'default' };
    case 'counting':
      return { text: t('chip.status.counting', { S: fmtSections(p.sectionsFraction) }), tone: 'default' };
    case 'complete':
      return { text: t('chip.status.complete'), tone: 'done' };
    case 'math_decision':
      return { text: p.label || t('chip.status.decidedMath'), tone: 'done' };
    case 'tse_result':
      return { text: p.label || t('chip.status.elected', { 'X.gender': p.gender }), tone: 'done' };
  }
}

/** Chip de situação da apuração (§3.8 `StatusChip`): traduz status e decisão num `Chip` padronizado. */
export function StatusChip(props: StatusChipProps) {
  const situation = situationOf(props.status, props.decision);
  const { text, tone } = view(situation, props);
  return (
    <Chip
      class="status-chip"
      data-testid="status-chip"
      data-state={situation}
      label={text}
      tone={tone}
      ariaHidden={props.ariaHidden}
    />
  );
}
